/*
 * Pointer-based drag manager (Stage 0).
 *
 * Why: Chrome 153 corrupts a tab's native HTML5 drag subsystem — drags
 * self-abort or wedge the whole tab (clicks dead, scroll alive), and only a
 * new tab recovers. Confirmed in the field via src/dragMonitor.js: the wedge
 * happens with NO DOM mutation and NO source detach, i.e. it is internal to
 * Chrome's native DnD and unfixable from the app while we use native drag.
 * Pointer/mouse events keep flowing throughout the wedge, so a drag built on
 * mouse events instead of native DnD is immune. This is the same approach
 * Trello / Notion / Angular CDK / SortableJS-fallback already use.
 *
 * Design goal: reuse the EXISTING native drop handlers unchanged. A source's
 * drag is carried two ways today — an eventBus emit that sets `dragData` on
 * receiving components, and a `dataTransfer.setData('text', json)` payload
 * read back with `getData`. The manager reproduces BOTH: it fires the same
 * eventBus emit on drag-start, and on drop it invokes the target's own drop
 * handler with a synthetic event whose `dataTransfer.getData` returns the same
 * json. So business logic (moveDocument, linkMail, field binding, recycle…)
 * runs identically; only the transport changed.
 *
 * Rollout: gated behind localStorage `__pointerDrag`. When off, sources keep
 * native draggable and nothing here activates. Toggle at runtime for testing
 * with `window.__pointerDrag(true|false)`.
 *
 * Registration (via the Vue directives installed below):
 *   v-pdrag="() => ({ json, emit, ghostText })"   // on a drag source
 *   v-pdrop="(syntheticEvent) => this.drop(syntheticEvent, stepId)"  // on a target
 */

const THRESHOLD_PX = 5;       // movement before a click becomes a drag
const SCROLL_EDGE_PX = 48;    // auto-scroll when cursor within this of an edge
const SCROLL_SPEED = 14;      // px per frame at the edge

const FLAG_KEY = '__pointerDrag';

// Default ON (rolled out 2026-09). Pointer-drag is active unless a machine has
// explicitly opted out via window.__pointerDrag(false) (stores '0'). If
// localStorage is unavailable, default to enabled — native drag is the thing
// that freezes tabs, so failing "on" is the safer choice.
function flagEnabled() {
    try { return localStorage.getItem(FLAG_KEY) !== '0'; } catch (e) { return true; }
}

// -------------------------------------------------------------------------
// Manager state
// -------------------------------------------------------------------------
const zones = new Map();   // element -> onDrop(syntheticEvent)
let pending = null;        // {el, getPayload, startX, startY} between mousedown and threshold
let active = null;         // live drag: {payload, sourceEl, ghost, zoneEl, scrollRAF}

function isInteractive(el) {
    // don't hijack clicks on controls inside a draggable card
    let n = el;
    for (let i = 0; n && i < 6; i++, n = n.parentElement) {
        const tag = n.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || tag === 'BUTTON' || tag === 'A') return true;
        if (n.getAttribute && n.getAttribute('contenteditable') === 'true') return true;
    }
    return false;
}

function findZoneEl(x, y) {
    let el = document.elementFromPoint(x, y);
    for (let i = 0; el && i < 25; i++, el = el.parentElement) {
        if (zones.has(el)) return el;
    }
    return null;
}

function makeGhost(sourceEl, text) {
    let ghost;
    try {
        ghost = sourceEl.cloneNode(true);
        const r = sourceEl.getBoundingClientRect();
        ghost.style.width = r.width + 'px';
        ghost.style.height = r.height + 'px';
    } catch (e) {
        ghost = document.createElement('div');
        ghost.textContent = text || 'Moving…';
    }
    ghost.classList.add('pdrag-ghost');
    Object.assign(ghost.style, {
        position: 'fixed', top: '0', left: '0', margin: '0', zIndex: '2147483646',
        pointerEvents: 'none', opacity: '0.82', boxShadow: '0 6px 20px rgba(0,0,0,0.35)',
        transform: 'translate(-9999px,-9999px)', transition: 'none', borderRadius: '4px',
        overflow: 'hidden', maxWidth: '360px'
    });
    document.body.appendChild(ghost);
    return ghost;
}

function moveGhost(x, y) {
    if (active && active.ghost)
        active.ghost.style.transform = `translate(${x + 12}px, ${y + 12}px)`;
}

function setHoverZone(el) {
    if (!active) return;
    if (active.zoneEl === el) return;
    if (active.zoneEl) active.zoneEl.classList.remove('pdrag-over');
    active.zoneEl = el;
    if (el) el.classList.add('pdrag-over');
}

// nearest scrollable ancestor of the element under the cursor
function scrollableUnder(x, y) {
    let el = document.elementFromPoint(x, y);
    for (let i = 0; el && i < 25; i++, el = el.parentElement) {
        const s = getComputedStyle(el);
        if (/(auto|scroll)/.test(s.overflowY) && el.scrollHeight > el.clientHeight + 2) return el;
    }
    return null;
}

function autoScrollTick() {
    if (!active) return;
    const {x, y} = active.last;
    const sc = scrollableUnder(x, y);
    if (sc) {
        const r = sc.getBoundingClientRect();
        if (y - r.top < SCROLL_EDGE_PX) sc.scrollTop -= SCROLL_SPEED;
        else if (r.bottom - y < SCROLL_EDGE_PX) sc.scrollTop += SCROLL_SPEED;
    }
    active.scrollRAF = requestAnimationFrame(autoScrollTick);
}

// -------------------------------------------------------------------------
// Drag lifecycle
// -------------------------------------------------------------------------
function beginDrag(x, y) {
    const {el, getPayload} = pending;
    pending = null;
    let payload;
    try { payload = getPayload(); } catch (e) { payload = null; }
    if (!payload) { cleanupListeners(); return; }

    active = {
        payload, sourceEl: el, ghost: makeGhost(el, payload.ghostText),
        zoneEl: null, last: {x, y}, scrollRAF: null
    };
    // populate receivers' dragData exactly as native dragstart does today
    try { if (typeof payload.emit === 'function') payload.emit(); } catch (e) { /* ignore */ }
    document.body.classList.add('pdrag-active');
    moveGhost(x, y);
    active.scrollRAF = requestAnimationFrame(autoScrollTick);
}

function onDocMouseMove(ev) {
    if (active) {
        active.last = {x: ev.clientX, y: ev.clientY};
        moveGhost(ev.clientX, ev.clientY);
        setHoverZone(findZoneEl(ev.clientX, ev.clientY));
        ev.preventDefault();
        return;
    }
    if (pending) {
        const dx = ev.clientX - pending.startX, dy = ev.clientY - pending.startY;
        if (dx * dx + dy * dy >= THRESHOLD_PX * THRESHOLD_PX) beginDrag(ev.clientX, ev.clientY);
    }
}

function syntheticDropEvent(payload, x, y) {
    return {
        preventDefault() {}, stopPropagation() {},
        clientX: x, clientY: y,
        dataTransfer: {
            getData: () => (payload && payload.json) || '',
            files: {length: 0},
            types: payload && payload.json ? ['text'] : []
        }
    };
}

function onDocMouseUp(ev) {
    // Released before crossing the threshold: it was a click, not a drag.
    // Clear the pending source and detach listeners — leaving `pending` set
    // would block every future drag (onSourceMouseDown bails while pending).
    if (!active) { pending = null; cleanupListeners(); return; }
    const zoneEl = findZoneEl(ev.clientX, ev.clientY);
    const payload = active.payload;
    // Diagnostic: last pointer drop — inspect `window.__lastPdrop` in the
    // console to see the exact payload json + drop target when a drop
    // misbehaves (e.g. an unexpected snack).
    try {
        window.__lastPdrop = {
            json: payload && payload.json,
            ghostText: payload && payload.ghostText,
            droppedOnZone: !!(zoneEl && zones.has(zoneEl)),
            zone: describeEl(zoneEl || document.elementFromPoint(ev.clientX, ev.clientY)),
            t: new Date().toISOString()
        };
    } catch (e) { /* ignore */ }
    teardown();
    if (zoneEl && zones.has(zoneEl)) {
        try { zones.get(zoneEl)(syntheticDropEvent(payload, ev.clientX, ev.clientY)); }
        catch (e) { /* eslint-disable-next-line */ console.error('[pdrag] drop handler error', e); }
    }
}

function cancel() {
    if (active) teardown();
    else { pending = null; cleanupListeners(); }
}

function teardown() {
    if (active) {
        if (active.zoneEl) active.zoneEl.classList.remove('pdrag-over');
        if (active.ghost && active.ghost.parentNode) active.ghost.parentNode.removeChild(active.ghost);
        if (active.scrollRAF) cancelAnimationFrame(active.scrollRAF);
    }
    active = null;
    pending = null;
    document.body.classList.remove('pdrag-active');
    cleanupListeners();
}

function onKeyDown(ev) { if (ev.key === 'Escape') cancel(); }
function onContextMenu() { cancel(); }
function onWindowBlur() { cancel(); }
function onVisibility() { if (document.hidden) cancel(); }

function addListeners() {
    document.addEventListener('mousemove', onDocMouseMove, true);
    document.addEventListener('mouseup', onDocMouseUp, true);
    document.addEventListener('keydown', onKeyDown, true);
    document.addEventListener('contextmenu', onContextMenu, true);
    window.addEventListener('blur', onWindowBlur, true);
    document.addEventListener('visibilitychange', onVisibility, true);
}
function cleanupListeners() {
    document.removeEventListener('mousemove', onDocMouseMove, true);
    document.removeEventListener('mouseup', onDocMouseUp, true);
    document.removeEventListener('keydown', onKeyDown, true);
    document.removeEventListener('contextmenu', onContextMenu, true);
    window.removeEventListener('blur', onWindowBlur, true);
    document.removeEventListener('visibilitychange', onVisibility, true);
}

// Nested elements can be natively draggable without a `draggable` attribute —
// notably <img> (PDF thumbnails, image previews) and <a>. Turning off
// `draggable` on the card doesn't stop those, so a native image drag could
// still start inside a pointer source and wedge the tab. This global guard
// cancels ANY native dragstart that originates inside a registered source
// while pointer mode is on; pointer handling takes over via mousedown.
function onGlobalDragStart(ev) {
    if (!flagEnabled()) return;
    const t = ev.target;
    // <img> and <a> are draggable by DEFAULT (no attribute needed) — email
    // body inline images, image attachments, PDF thumbnails, link text. Any of
    // them can start a native drag that wedges Chrome 153, and native
    // image/link dragging isn't a feature this app relies on. With pointer
    // mode on we own dragging, so kill all native img/<a> drags outright.
    if (t && (t.tagName === 'IMG' || t.tagName === 'A' || (t.closest && t.closest('a[href],img')))) {
        ev.preventDefault();
        return;
    }
    // Belt-and-braces: any other native drag that starts inside a registered
    // pointer source (e.g. a nested element with implicit draggability).
    let n = t;
    for (let i = 0; n && i < 25; i++, n = n.parentElement) {
        if (n.__pdragPayload) { ev.preventDefault(); return; }
    }
}

function onSourceMouseDown(ev) {
    if (ev.button !== 0) return;                 // left button only
    if (active || pending) return;
    const el = ev.currentTarget;
    if (isInteractive(ev.target)) return;        // let clicks on controls through
    pending = {el, getPayload: el.__pdragPayload, startX: ev.clientX, startY: ev.clientY};
    addListeners();
    // do NOT preventDefault here — preserves native click/dblclick under threshold
}

// -------------------------------------------------------------------------
// Public registration + Vue directives
// -------------------------------------------------------------------------
function makeSource(el, getPayload) {
    el.__pdragPayload = getPayload;
    if (flagEnabled()) {
        el.setAttribute('draggable', 'false');
        el.addEventListener('mousedown', onSourceMouseDown, false);
    }
}
function unmakeSource(el) {
    el.removeEventListener('mousedown', onSourceMouseDown, false);
    delete el.__pdragPayload;
}
function registerZone(el, onDrop) { if (flagEnabled()) zones.set(el, onDrop); }
function unregisterZone(el) { zones.delete(el); }

function injectStyle() {
    if (document.getElementById('pdrag-style')) return;
    const s = document.createElement('style');
    s.id = 'pdrag-style';
    s.textContent =
        '.pdrag-active, .pdrag-active * { cursor: grabbing !important; user-select: none !important; }' +
        '.pdrag-over { outline: 2px dashed var(--primary, #1976d2) !important; outline-offset: -2px; ' +
        'background: rgba(25,118,210,0.08) !important; }' +
        '.pdrag-ghost { will-change: transform; }';
    document.head.appendChild(s);
}

function install(Vue) {
    injectStyle();
    // Templates read this to switch native `draggable` off when pointer mode is
    // active (otherwise a :draggable binding re-enables native drag on rerender
    // and both fire). Set once at boot; toggling the flag requires a reload.
    Vue.prototype.$pointerDrag = flagEnabled();
    // runtime toggle for testing/rollout
    try {
        window.__pointerDrag = on => {
            try { localStorage.setItem(FLAG_KEY, on ? '1' : '0'); } catch (e) { /* ignore */ }
            return 'reload the tab to apply';
        };
    } catch (e) { /* ignore */ }

    // Global native-drag suppressor for nested img/<a> inside pointer sources.
    document.addEventListener('dragstart', onGlobalDragStart, true);

    Vue.directive('pdrag', {
        bind(el, binding) { makeSource(el, binding.value); },
        update(el, binding) { el.__pdragPayload = binding.value; },
        unbind(el) { unmakeSource(el); }
    });
    Vue.directive('pdrop', {
        bind(el, binding) { registerZone(el, binding.value); },
        update(el, binding) { if (flagEnabled()) zones.set(el, binding.value); },
        unbind(el) { unregisterZone(el); }
    });
}

module.exports = {install, flagEnabled};
